import BoltOutlined from "@mui/icons-material/BoltOutlined";
import EventAvailableOutlined from "@mui/icons-material/EventAvailableOutlined";
import HistoryToggleOffOutlined from "@mui/icons-material/HistoryToggleOffOutlined";
import NotificationsActiveOutlined from "@mui/icons-material/NotificationsActiveOutlined";
import PhoneIphoneOutlined from "@mui/icons-material/PhoneIphoneOutlined";
import ScheduleSendOutlined from "@mui/icons-material/ScheduleSendOutlined";
import { Box } from "@mui/material";
import { DateTime } from "luxon";
import { type Dispatch, type FC, type SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { type FlatSwitchOption, SystemFlatSwitch } from "../../../../components/UIToolkit/Clickable/SystemFlatSwitch";
import { SystemSwitch } from "../../../../components/UIToolkit/Clickable/SystemSwitch";
import { DateInput } from "../../../../components/UIToolkit/Inputs/DateInput";
import { SystemDropdown } from "../../../../components/UIToolkit/Inputs/SystemDropDown";
import { SystemValidationTextField } from "../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { SettingRow } from "../../../../components/UIToolkit/molecules/SettingRow";
import { SettingsSection } from "../../../../components/UIToolkit/molecules/SettingsSection";
import { FEATURE_FLAGS } from "../../../../contexts/FeatureFlagsProvider";
import { useFeatureFlag } from "../../../../hooks/useOpenFeatureFlag";
import { NUMERIC_FIELD_WIDTH } from "../../../../theme/tokens";
import { SHIFT_ALERT_TIMES_MINUTES } from "../utils";
import { AutoApproveSection } from "./autoApprove/AutoApproveSection";
import { SIDE_LAID_GROUND_SX } from "./ratesConsts";
import type { GeneralFormData } from "./types";

const MINUTES_PER_HOUR = 60;
const SECTION_CONTENT_MAX_WIDTH = 900;
// The OT-release delay is entered as a value plus one of these units. Which of the form's two
// mutually-exclusive fields (`...Days` / `...Hours`) is non-null encodes the active unit.
type OvertimeReleaseUnit = "days" | "hours";
// Matches the dropdown width used by the shift-alert rows, so the controls column stays aligned.
const DATE_FIELD_WIDTH = 240;
// Sentinel "no reminder" — 0 isn't a real alert time, so it maps cleanly to/from `undefined`.
const REMINDER_OFF = 0;

type CallOffWindowField = {
	field: keyof GeneralFormData;
	translationKey: string;
	placeholderKey: string;
	testId: string;
	allowNegative?: boolean;
};

// The four call-off window bounds, rendered as identical numeric rows. Bedside = staff calling off
// their own shift (app / phone line); admin = a scheduler removing someone. Each pair is independent,
// so a department can widen the staff-facing window without changing what counts as a scheduler call-off.
const CALL_OFF_WINDOW_FIELDS: readonly CallOffWindowField[] = [
	{
		field: "callOffBypassHoursBeforeShiftBedside",
		translationKey: "beforeShiftBedside",
		placeholderKey: "department.config.staffApp.callOffWindow.placeholder.before",
		testId: "dept-config-staff-app-call-off-window-before-bedside",
	},
	{
		field: "callOffBypassHoursAfterStartBedside",
		translationKey: "afterStartBedside",
		placeholderKey: "department.config.staffApp.callOffWindow.placeholder.after",
		testId: "dept-config-staff-app-call-off-window-after-bedside",
		allowNegative: true,
	},
	{
		field: "callOffBypassHoursBeforeShiftAdmin",
		translationKey: "beforeShiftAdmin",
		placeholderKey: "department.config.staffApp.callOffWindow.placeholder.before",
		testId: "dept-config-staff-app-call-off-window-before-admin",
	},
	{
		field: "callOffBypassHoursAfterStartAdmin",
		translationKey: "afterStartAdmin",
		placeholderKey: "department.config.staffApp.callOffWindow.placeholder.after",
		testId: "dept-config-staff-app-call-off-window-after-admin",
	},
];

export const StaffAppTabContent: FC<{
	formData: GeneralFormData;
	setFormData: Dispatch<SetStateAction<GeneralFormData>>;
}> = ({ formData, setFormData }) => {
	const { t } = useTranslation();
	const autoApproveEnabled = useFeatureFlag(FEATURE_FLAGS.AutoApproveShifts);
	const scheduleVisibilityDate = formData.staffScheduleDisplayUntilDate;

	// Turning the horizon on needs a starting date the picker can show; the end of the current week is
	// the same horizon the Staffing "Staff Schedule Visibility" popup produces for "0 weeks ahead".
	// DEFAULT: seeded only on the toggle-on click and visible in the field before Save, so it's an
	// editable starting point rather than a hidden fallback.
	const handleScheduleVisibilityToggle = (enabled: boolean) =>
		setFormData((prev) => ({ ...prev, staffScheduleDisplayUntilDate: enabled ? DateTime.now().endOf("week").startOf("day") : null }));

	const reminderLabel = (minutes: number) => {
		if (minutes < MINUTES_PER_HOUR) return t("department.config.staffApp.reminder.minutes", { count: minutes });
		const hours = minutes / MINUTES_PER_HOUR;
		return hours > 1 ? t("department.config.staffApp.reminder.hours", { count: hours }) : t("department.config.staffApp.reminder.hour", { count: hours });
	};

	// Lead with an "Off" row so an unset reminder reads as a deliberate choice, not a blank field.
	const reminderItems = [
		{ value: REMINDER_OFF, label: t("department.config.staffApp.reminder.none") },
		...SHIFT_ALERT_TIMES_MINUTES.map((minutes: number) => ({ value: minutes, label: reminderLabel(minutes) })),
	];

	// OT pickup-release control (value + Days/Hours unit). The stored value lives in exactly one of the
	// form's two fields, so the unit is normally derived from which is non-null. The override only
	// decides the toggle position while BOTH are empty (feature off) — so switching to Hours and then
	// clearing the field doesn't snap the toggle back to Days.
	const [overtimeReleaseUnitOverride, setOvertimeReleaseUnitOverride] = useState<OvertimeReleaseUnit | null>(null);
	const overtimeReleaseUnit: OvertimeReleaseUnit =
		formData.overtimeEligibilityReleaseHours != null
			? "hours"
			: formData.overtimeEligibilityReleaseDays != null
				? "days"
				: (overtimeReleaseUnitOverride ?? "days");
	const overtimeReleaseValue = overtimeReleaseUnit === "hours" ? formData.overtimeEligibilityReleaseHours : formData.overtimeEligibilityReleaseDays;
	const overtimeReleaseUnitOptions: FlatSwitchOption<OvertimeReleaseUnit>[] = [
		{
			value: "days",
			label: t("department.config.staffApp.overtimePickupRelease.unit.days"),
			"data-testid": "dept-config-staff-app-overtime-pickup-release-unit-days",
		},
		{
			value: "hours",
			label: t("department.config.staffApp.overtimePickupRelease.unit.hours"),
			"data-testid": "dept-config-staff-app-overtime-pickup-release-unit-hours",
		},
	];

	// Write to the active unit's field and clear the other, so exactly one is ever populated: unit=Days
	// sends `...Days` + `...Hours: null` (legacy-compatible), unit=Hours sends the inverse. `null` (not
	// undefined) is an explicit off/reset the backend persists; 0 stays a valid saved value (immediate).
	const setOvertimeReleaseValue = (value: number | null) =>
		setFormData((prev) => ({
			...prev,
			overtimeEligibilityReleaseDays: overtimeReleaseUnit === "days" ? value : null,
			overtimeEligibilityReleaseHours: overtimeReleaseUnit === "hours" ? value : null,
		}));

	const switchOvertimeReleaseUnit = (unit: OvertimeReleaseUnit) => {
		setOvertimeReleaseUnitOverride(unit);
		// Keep the entered number, reinterpreted under the new unit; move it into that unit's field.
		setFormData((prev) => {
			const current = prev.overtimeEligibilityReleaseHours ?? prev.overtimeEligibilityReleaseDays ?? null;
			return {
				...prev,
				overtimeEligibilityReleaseDays: unit === "days" ? current : null,
				overtimeEligibilityReleaseHours: unit === "hours" ? current : null,
			};
		});
	};

	return (
		<Box component="div" sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
			<Box component="div" sx={SIDE_LAID_GROUND_SX}>
				<SettingsSection
					headerPlacement="side"
					icon={<PhoneIphoneOutlined />}
					title={t("department.config.staffApp.section.staffApp")}
					description={t("department.config.staffApp.section.staffApp.description")}
					layout="stack"
					contentMaxWidth={SECTION_CONTENT_MAX_WIDTH}
					divider
					dataTestId="dept-config-staff-app-general"
				>
					<SettingRow
						label={t("department.config.staffApp.canViewNurses.label")}
						description={t("department.config.staffApp.canViewNurses.description")}
						control={
							<SystemSwitch
								checked={!!formData.canViewNurses}
								onChange={() => setFormData((prev) => ({ ...prev, canViewNurses: !formData.canViewNurses }))}
								dataTestId="dept-config-staff-app-can-view-nurses"
							/>
						}
						dataTestId="dept-config-staff-app-can-view-nurses-row"
					/>
					<SettingRow
						label={t("staffing.target.warning.hide.setting")}
						description={t("staffing.target.warning.hide.setting.description")}
						control={
							<SystemSwitch
								checked={!!formData.hideOverstaffingWarning}
								onChange={() => setFormData((prev) => ({ ...prev, hideOverstaffingWarning: !prev.hideOverstaffingWarning }))}
								dataTestId="staff-app-hide-overstaffing-warning-toggle"
							/>
						}
						dataTestId="dept-config-staff-app-hide-overstaffing-warning-row"
					/>
				</SettingsSection>

				<SettingsSection
					headerPlacement="side"
					icon={<EventAvailableOutlined />}
					title={t("department.config.staffApp.section.scheduleVisibility")}
					description={t("department.config.staffApp.section.scheduleVisibility.description")}
					layout="stack"
					contentMaxWidth={SECTION_CONTENT_MAX_WIDTH}
					divider
					dataTestId="dept-config-staff-app-schedule-visibility"
				>
					<SettingRow
						label={t("department.config.staffApp.scheduleVisibility.limit.label")}
						description={t("department.config.staffApp.scheduleVisibility.limit.description")}
						control={
							<SystemSwitch
								checked={scheduleVisibilityDate !== null}
								onChange={(event) => handleScheduleVisibilityToggle(event.target.checked)}
								dataTestId="dept-config-staff-app-schedule-visibility-toggle"
							/>
						}
						dataTestId="dept-config-staff-app-schedule-visibility-row"
					/>
					{scheduleVisibilityDate !== null && (
						<SettingRow
							label={t("department.config.staffApp.scheduleVisibility.untilDate.label")}
							description={t("department.config.staffApp.scheduleVisibility.untilDate.description")}
							fullWidthControl
							control={
								<DateInput
									label={t("department.config.staffApp.scheduleVisibility.untilDate.label")}
									value={scheduleVisibilityDate}
									onChange={(newDate) => setFormData((prev) => ({ ...prev, staffScheduleDisplayUntilDate: newDate.startOf("day") }))}
									formControlSx={{ width: { xs: "100%", sm: DATE_FIELD_WIDTH } }}
									dataTestId="dept-config-staff-app-schedule-visibility-date"
								/>
							}
							dataTestId="dept-config-staff-app-schedule-visibility-date-row"
						/>
					)}
				</SettingsSection>

				<SettingsSection
					headerPlacement="side"
					icon={<NotificationsActiveOutlined />}
					title={t("department.config.staffApp.section.shiftAlerts")}
					description={t("department.config.staffApp.section.shiftAlerts.description")}
					layout="stack"
					contentMaxWidth={SECTION_CONTENT_MAX_WIDTH}
					dataTestId="dept-config-staff-app-shift-alerts"
				>
					<SettingRow
						label={t("department.config.staffApp.beforeShiftReminder.label")}
						description={t("department.config.staffApp.beforeShiftReminder.description")}
						control={
							<SystemDropdown
								aria-label={t("department.config.staffApp.beforeShiftReminder.label")}
								formControlSx={{ width: { xs: "100%", sm: 240 } }}
								items={reminderItems}
								selectedItem={formData.notifyBeforeShiftStartMintutes ?? REMINDER_OFF}
								onChange={(value) => setFormData((prev) => ({ ...prev, notifyBeforeShiftStartMintutes: value ? value : undefined }))}
								data-testid="dept-config-staff-app-before-shift-reminder"
							/>
						}
						dataTestId="dept-config-staff-app-before-shift-reminder-row"
					/>
					<SettingRow
						label={t("department.config.staffApp.endOfShiftReminder.label")}
						description={t("department.config.staffApp.endOfShiftReminder.description")}
						control={
							<SystemDropdown
								aria-label={t("department.config.staffApp.endOfShiftReminder.label")}
								formControlSx={{ width: { xs: "100%", sm: 240 } }}
								items={reminderItems}
								selectedItem={formData.notifyBeforeShiftEndMintutes ?? REMINDER_OFF}
								onChange={(value) => setFormData((prev) => ({ ...prev, notifyBeforeShiftEndMintutes: value ? value : undefined }))}
								data-testid="dept-config-staff-app-end-of-shift-reminder"
							/>
						}
						dataTestId="dept-config-staff-app-end-of-shift-reminder-row"
					/>
				</SettingsSection>
				<SettingsSection
					headerPlacement="side"
					icon={<ScheduleSendOutlined />}
					title={t("department.config.staffApp.section.callOffMessages")}
					description={t("department.config.staffApp.section.callOffMessages.description")}
					layout="stack"
					contentMaxWidth={SECTION_CONTENT_MAX_WIDTH}
					dataTestId="dept-config-staff-app-call-off-messages"
				>
					<SettingRow
						label={t("department.config.staffApp.callOffMessageLeadTime.label")}
						description={t("department.config.staffApp.callOffMessageLeadTime.description")}
						control={
							<SystemValidationTextField
								inputType="number"
								// Clearing sends explicit null (not undefined) so the update actually resets the column to
								// NULL — `update_dept_config` dumps with exclude_unset, which drops an omitted (undefined) key
								// and would silently keep the old lead time. NULL = the agent messages everyone at once.
								value={formData.callOffMessageLeadTimeMinutes != null ? String(formData.callOffMessageLeadTimeMinutes) : ""}
								onChange={(event) =>
									setFormData((prev) => ({
										...prev,
										callOffMessageLeadTimeMinutes: event.target.value === "" ? null : Number(event.target.value),
									}))
								}
								injectedProps={{ fullWidth: true }}
								formControlSx={{ width: { xs: "100%", sm: NUMERIC_FIELD_WIDTH.SETTINGS } }}
								inputProps={{ inputProps: { min: 0, step: 1 } }}
								placeholder={t("department.config.staffApp.callOffMessageLeadTime.placeholder")}
								dataTestId="dept-config-staff-app-call-off-message-lead-time"
							/>
						}
						dataTestId="dept-config-staff-app-call-off-message-lead-time-row"
					/>
				</SettingsSection>

				<SettingsSection
					headerPlacement="side"
					icon={<HistoryToggleOffOutlined />}
					title={t("department.config.staffApp.section.overtimePickupRelease")}
					description={t("department.config.staffApp.section.overtimePickupRelease.description")}
					layout="stack"
					contentMaxWidth={SECTION_CONTENT_MAX_WIDTH}
					dataTestId="dept-config-staff-app-overtime-pickup-release"
				>
					<SettingRow
						label={t("department.config.staffApp.overtimePickupRelease.label")}
						description={t("department.config.staffApp.overtimePickupRelease.description")}
						control={
							<Box
								sx={{
									display: "flex",
									flexDirection: { xs: "column", sm: "row" },
									alignItems: { xs: "stretch", sm: "center" },
									gap: 1,
									width: { xs: "100%", sm: "auto" },
								}}
							>
								<SystemValidationTextField
									inputType="number"
									value={overtimeReleaseValue != null ? String(overtimeReleaseValue) : ""}
									onChange={(event) => setOvertimeReleaseValue(event.target.value === "" ? null : Number(event.target.value))}
									injectedProps={{ fullWidth: true }}
									formControlSx={{ width: { xs: "100%", sm: NUMERIC_FIELD_WIDTH.SETTINGS } }}
									// Whole numbers only — both days and hours are integers; non-negative, 0 = immediate.
									inputProps={{ inputProps: { min: 0, step: 1 } }}
									placeholder={t("department.config.staffApp.overtimePickupRelease.placeholder")}
									dataTestId="dept-config-staff-app-overtime-pickup-release-days"
								/>
								<SystemFlatSwitch<OvertimeReleaseUnit>
									options={overtimeReleaseUnitOptions}
									activeIndexOrValue={overtimeReleaseUnit}
									onSwitch={(option) => switchOvertimeReleaseUnit(option.value)}
									// The unit reinterprets a value already on screen, so apply it immediately rather than
									// after the default slide delay — the number field must re-read from the new field at once.
									delayCallback={0}
									equalWidthOptions
									// Match the 40px field height beside it so the row's controls share a centre line.
									paddingOverrides={{ xs: "9px 16px", xl: "9px 16px" }}
									data-testid="dept-config-staff-app-overtime-pickup-release-unit"
								/>
							</Box>
						}
						dataTestId="dept-config-staff-app-overtime-pickup-release-days-row"
					/>
				</SettingsSection>

				<SettingsSection
					headerPlacement="side"
					icon={<HistoryToggleOffOutlined />}
					title={t("department.config.staffApp.section.callOffWindow")}
					description={t("department.config.staffApp.section.callOffWindow.description")}
					layout="stack"
					contentMaxWidth={SECTION_CONTENT_MAX_WIDTH}
					dataTestId="dept-config-staff-app-call-off-window"
				>
					{CALL_OFF_WINDOW_FIELDS.map(({ field, translationKey, placeholderKey, testId, allowNegative }) => (
						<SettingRow
							key={field}
							label={t(`department.config.staffApp.callOffWindow.${translationKey}.label`)}
							description={t(`department.config.staffApp.callOffWindow.${translationKey}.description`)}
							control={
								<SystemValidationTextField
									inputType="number"
									// Clearing sends null, which resets the column to the backend's default window.
									value={formData[field] != null ? String(formData[field]) : ""}
									onChange={(event) =>
										setFormData((prev) => ({
											...prev,
											[field]: event.target.value === "" ? null : Number(event.target.value),
										}))
									}
									injectedProps={{ fullWidth: true }}
									formControlSx={{ width: { xs: "100%", sm: NUMERIC_FIELD_WIDTH.SETTINGS } }}
									// `step: "any"` because the backend stores an interval and accepts fractional hours
									// (e.g. 6.5). A step of 1 makes those a native stepMismatch, which silently blocks
									// the enclosing form's submit — the Save click would do nothing.
									inputProps={{ inputProps: { min: allowNegative ? undefined : 0, step: "any" } }}
									placeholder={t(placeholderKey)}
									dataTestId={testId}
								/>
							}
							dataTestId={`${testId}-row`}
						/>
					))}
				</SettingsSection>
			</Box>

			{/* Only shown where the AutoApproveShifts flag is on for this department. */}
			{autoApproveEnabled && (
				<Box component="div" sx={SIDE_LAID_GROUND_SX}>
					<SettingsSection
						headerPlacement="side"
						icon={<BoltOutlined />}
						title={t("department.config.staffApp.autoApprove.section")}
						description={t("department.config.staffApp.autoApprove.section.description")}
						layout="stack"
						contentMaxWidth={SECTION_CONTENT_MAX_WIDTH}
						dataTestId="dept-config-staff-app-auto-approve"
					>
						<AutoApproveSection formData={formData} setFormData={setFormData} />
					</SettingsSection>
				</Box>
			)}
		</Box>
	);
};
